import { ReactElement, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  historyAPI,
  HistoryCollectionResponse,
  HistoryArticleCard,
} from '../../services/api';
import {
  ChevronLeft,
  ChevronRight,
  Loader2,
  Clock,
  MapPin,
  Calendar,
} from 'lucide-react';

function ArticleCard({ article }: { article: HistoryArticleCard }): ReactElement {
  return (
    <a
      href={historyAPI.getArticleHtmlUrl(article.id)}
      className="group block p-4 rounded-lg bg-white border border-gray-100 hover:border-gray-200 hover:shadow-sm transition-all duration-150"
    >
      {/* Top badges */}
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        {article.era && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 bg-slate-100 rounded">
            <Calendar className="w-2.5 h-2.5" />
            {article.era}
          </span>
        )}
        {article.country && (
          <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 bg-slate-50 rounded">
            <MapPin className="w-2.5 h-2.5" />
            {article.country}
          </span>
        )}
        {article.key_stat && (
          <span className="px-1.5 py-0.5 text-[10px] font-mono font-medium text-emerald-600 bg-emerald-50 rounded">
            {article.key_stat}
          </span>
        )}
        {article.time_ago && (
          <span className="text-[10px] text-gray-400 ml-auto">{article.time_ago}</span>
        )}
      </div>

      {/* Headline */}
      <h3 className="text-sm font-medium text-gray-900 group-hover:text-gray-700 transition-colors leading-snug line-clamp-2 mb-1"
        title={article.subtitle || undefined}
      >
        {article.headline}
      </h3>

      {/* Summary */}
      {article.summary && (
        <p className="text-xs text-gray-500 line-clamp-2 mb-2">
          {article.summary}
        </p>
      )}

      {/* Footer */}
      <div className="flex items-center gap-2 text-[10px] text-gray-400">
        {article.read_time_minutes && (
          <span className="flex items-center gap-1">
            <Clock className="w-2.5 h-2.5" />
            {article.read_time_minutes} min read
          </span>
        )}
        {article.score && (
          <>
            <span>&middot;</span>
            <span>{'★'.repeat(article.score)}{'☆'.repeat(5 - article.score)}</span>
          </>
        )}
        {article.content_type && (
          <>
            <span>&middot;</span>
            <span className="uppercase tracking-wide">{article.content_type}</span>
          </>
        )}
      </div>
    </a>
  );
}


// Sliding 7-button pagination
function Pagination({
  page,
  totalPages,
  onPageChange,
}: {
  page: number;
  totalPages: number;
  onPageChange: (p: number) => void;
}): ReactElement | null {
  if (totalPages <= 1) return null;

  const windowSize = 7;
  let start = Math.max(1, page - Math.floor(windowSize / 2));
  const end = Math.min(totalPages, start + windowSize - 1);
  if (end - start + 1 < windowSize) {
    start = Math.max(1, end - windowSize + 1);
  }
  const pages: number[] = [];
  for (let i = start; i <= end; i++) pages.push(i);

  return (
    <div className="flex items-center justify-center gap-1 mt-8">
      <button
        onClick={() => onPageChange(page - 1)}
        disabled={page <= 1}
        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronLeft className="w-4 h-4" />
      </button>
      {pages.map((p) => (
        <button
          key={p}
          onClick={() => onPageChange(p)}
          className={`w-8 h-8 rounded-lg text-xs font-medium transition-colors ${
            p === page
              ? 'bg-slate-900 text-white'
              : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700'
          }`}
        >
          {p}
        </button>
      ))}
      <button
        onClick={() => onPageChange(page + 1)}
        disabled={page >= totalPages}
        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
      >
        <ChevronRight className="w-4 h-4" />
      </button>
    </div>
  );
}


export default function CollectionPage(): ReactElement {
  const { collectionSlug } = useParams<{ collectionSlug: string }>();
  const [page, setPage] = useState(1);

  const { data, isLoading, error } = useQuery({
    queryKey: ['history', 'collection', collectionSlug, page],
    queryFn: () => historyAPI.getCollection(collectionSlug!, { page, per_page: 24 }).then(r => r.data),
    enabled: !!collectionSlug,
  });

  const handlePageChange = (p: number) => {
    setPage(p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-500">Failed to load collection.</p>
        <Link to="/history" className="text-sm text-blue-600 hover:underline mt-2 inline-block">
          Back to History
        </Link>
      </div>
    );
  }

  const resp = data as HistoryCollectionResponse;
  const { collection, articles, total_count, total_pages } = resp;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      {/* Breadcrumb */}
      <Link
        to="/history"
        className="inline-flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 transition-colors mb-4"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        History
      </Link>

      {/* Collection Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500 bg-slate-100 rounded">
            {resp.theme_name}
          </span>
          <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400 bg-slate-50 rounded">
            {collection.content_type}
          </span>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-1">{collection.title}</h1>
        {resp.collection_description && (
          <p className="text-sm text-gray-500 mb-2">{resp.collection_description}</p>
        )}
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span>{total_count} published article{total_count !== 1 ? 's' : ''}</span>
          <span>{collection.entry_count} total entries</span>
        </div>
      </div>

      {/* Article Grid */}
      {articles.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {articles.map((a) => (
            <ArticleCard key={a.id} article={a} />
          ))}
        </div>
      ) : (
        <div className="text-center py-16">
          <p className="text-gray-400 text-sm">No published articles yet for this collection.</p>
        </div>
      )}

      {/* Pagination */}
      <Pagination page={page} totalPages={total_pages} onPageChange={handlePageChange} />
    </div>
  );
}
